import { type ComponentType, useCallback } from 'react';
import { createRender } from './types';
import {
	PopoverContent,
	PopoverProvider,
	PopoverTarget,
	SpotlightActions,
	SpotlightBody,
	SpotlightCard,
	SpotlightControls,
	SpotlightDismissControl,
	SpotlightFooter,
	SpotlightHeader,
	SpotlightHeadline,
	SpotlightPrimaryAction,
} from '@atlaskit/spotlight';
import { useMountToHtmlElement } from '@post-office/components--post-office-team';
import { useStepContext } from '@post-office/message-step-context';
import { useMessageEvent } from '@post-office/message-lifecycle';
import { useMessageInstanceEvent } from '@post-office/message-instance-event-frontend-client';

interface FirstStepProps {
	dismiss: () => void;
	next: () => void;
}

interface LastStepProps {
	dismiss: () => void;
	back: () => void;
	done: () => void;
}

const stepMap: { [step: number]: string } = {
	1: '[data-inline-card]',
	2: '[data-embed-card]',
};

const tour: (ComponentType<FirstStepProps> | ComponentType<LastStepProps>)[] = [
	// Tour not in progress
	() => <></>,

	// Step 1
	({ dismiss, next }: FirstStepProps) => {
		return (
			<PopoverProvider>
				<PopoverTarget>
					{/* Dummy target */}
					<div />
				</PopoverTarget>
				<PopoverContent dismiss={dismiss} placement="right-end" isVisible={true}>
					<SpotlightCard testId="spotlight">
						<SpotlightHeader>
							<SpotlightHeadline>Step 1</SpotlightHeadline>
							<SpotlightControls>
								<SpotlightDismissControl />
							</SpotlightControls>
						</SpotlightHeader>
						<SpotlightBody>Content for step 1</SpotlightBody>
						<SpotlightFooter>
							<SpotlightActions>
								<SpotlightPrimaryAction onClick={dismiss}>Dismiss</SpotlightPrimaryAction>
								<SpotlightPrimaryAction onClick={next}>Next</SpotlightPrimaryAction>
							</SpotlightActions>
						</SpotlightFooter>
					</SpotlightCard>
				</PopoverContent>
			</PopoverProvider>
		);
	},

	// Step 2
	({ dismiss, back, done }: LastStepProps) => {
		return (
			<PopoverProvider>
				<PopoverTarget>
					{/* Dummy target */}
					<div />
				</PopoverTarget>
				<PopoverContent dismiss={dismiss} placement="right-end" isVisible={true}>
					<SpotlightCard testId="spotlight">
						<SpotlightHeader>
							<SpotlightHeadline>Step 2</SpotlightHeadline>
							<SpotlightControls>
								<SpotlightDismissControl />
							</SpotlightControls>
						</SpotlightHeader>
						<SpotlightBody>Content for step 2</SpotlightBody>
						<SpotlightFooter>
							<SpotlightActions>
								<SpotlightPrimaryAction onClick={dismiss}>Dismiss</SpotlightPrimaryAction>
								<SpotlightPrimaryAction onClick={back}>Back</SpotlightPrimaryAction>
								<SpotlightPrimaryAction onClick={done}>Done</SpotlightPrimaryAction>
							</SpotlightActions>
						</SpotlightFooter>
					</SpotlightCard>
				</PopoverContent>
			</PopoverProvider>
		);
	},
];

export const render = createRender(({ onDismissed, messageInstanceId }) => {
	const { messageDismissed } = useMessageEvent();
	const { messageInstanceClicked } = useMessageInstanceEvent();
	const { next, previous: back, currentStep: step } = useStepContext();
	const dismiss = useCallback(() => {
		void messageDismissed();
		void messageInstanceClicked({
			messageEventType: 'clicked',
			clickedElement: {
				clickIdentifier: 'dismiss',
				clickedElement: 'button',
				buttonType: 'secondary',
			},
			action: { actionType: 'dismiss' },
			additionalMetadataFromFrontend: {},
		});
		void onDismissed(messageInstanceId);
	}, [messageDismissed]);

	const renderInline = useMountToHtmlElement({
		selectors: stepMap[step],
		as: 'inline',
		position: 'inside',
	});

	const Step = tour[step] || (() => <></>);

	return (
		<div>{renderInline(<Step dismiss={dismiss} back={back} next={next} done={dismiss} />)}</div>
	);
});

export default render;
